export interface ScoreCheck {
  id: string;
  label: string;
  passed: boolean;
  score: number;
  maxScore: number;
  tip?: string;
}

export interface EmailScoreResult {
  totalScore: number;
  maxScore: number;
  checks: ScoreCheck[];
  grade: 'A' | 'B' | 'C' | 'D' | 'F';
}

const SPAM_WORDS = [
  'gratis',
  'offerta',
  'sconto',
  'urgente',
  'clicca qui',
  'compra',
  'vinci',
  'premi',
  'gratuito',
  'affare',
  'limitata',
  'esclusivo',
];

function stripHtml(html: string): string {
  return html.replace(/<[^>]*>/g, '');
}

function containsSpamWords(text: string): boolean {
  const lower = text.toLowerCase();
  return SPAM_WORDS.some((word) => lower.includes(word));
}

function hasAllCapsWord(text: string): boolean {
  return text.split(/\s+/).some((word) => {
    const letters = word.replace(/[^a-zA-ZÀ-ÿ]/g, '');
    return letters.length >= 2 && letters === letters.toUpperCase();
  });
}

function hasExcessPunctuation(text: string): boolean {
  return /[!]{2,}|[?]{2,}/.test(text);
}

function hasGreeting(text: string): boolean {
  const trimmed = text.trimStart();
  return /^(ciao|buongiorno|gentile|hi|dear|salve|buonasera)/i.test(trimmed);
}

function hasClosing(text: string): boolean {
  const lower = text.toLowerCase();
  return /(a presto|cordiali saluti|grazie|il team|best|distinti saluti|un saluto|saluti)/.test(
    lower,
  );
}

function countParagraphs(body: string): number {
  const hasDoubleLine = body.split(/\n\s*\n/).filter((p) => p.trim().length > 0).length >= 2;
  if (hasDoubleLine) return 2;

  const hasBrBreaks = body.split(/<br\s*\/?>\s*<br\s*\/?>/i).filter((p) => stripHtml(p).trim().length > 0).length >= 2;
  if (hasBrBreaks) return 2;

  const pTags = body.match(/<p[\s>]/gi);
  if (pTags && pTags.length >= 2) return pTags.length;

  return 1;
}

function getGrade(score: number): 'A' | 'B' | 'C' | 'D' | 'F' {
  if (score >= 85) return 'A';
  if (score >= 70) return 'B';
  if (score >= 55) return 'C';
  if (score >= 40) return 'D';
  return 'F';
}

export function calculateEmailScore(
  subject: string,
  body: string,
  placeholders: { key: string }[],
): EmailScoreResult {
  const plainBody = stripHtml(body);
  const placeholderPatterns = placeholders.map((p) => p.key);

  const checks: ScoreCheck[] = [];

  // --- General (10 pts) ---
  checks.push({
    id: 'has_subject',
    label: 'Oggetto presente',
    passed: subject.trim().length > 0,
    score: subject.trim().length > 0 ? 5 : 0,
    maxScore: 5,
  });

  checks.push({
    id: 'has_body',
    label: 'Corpo presente',
    passed: plainBody.trim().length > 0,
    score: plainBody.trim().length > 0 ? 5 : 0,
    maxScore: 5,
  });

  // --- Subject Line (35 pts) ---
  const subjectLen = subject.trim().length;
  const subjectLenOk = subjectLen >= 30 && subjectLen <= 60;
  checks.push({
    id: 'subject_length',
    label: 'Lunghezza oggetto (30-60 caratteri)',
    passed: subjectLenOk,
    score: subjectLenOk ? 10 : 0,
    maxScore: 10,
    tip: subjectLenOk ? undefined : "L'oggetto ideale è tra 30 e 60 caratteri",
  });

  const subjectHasPlaceholder = placeholderPatterns.some((p) => subject.includes(p));
  checks.push({
    id: 'subject_personalization',
    label: 'Personalizzazione oggetto',
    passed: subjectHasPlaceholder,
    score: subjectHasPlaceholder ? 8 : 0,
    maxScore: 8,
    tip: subjectHasPlaceholder
      ? undefined
      : 'Aggiungi un placeholder come {{nome}} per personalizzare',
  });

  const subjectNoCaps = !hasAllCapsWord(subject);
  checks.push({
    id: 'subject_no_caps',
    label: 'Nessuna parola TUTTA MAIUSCOLA',
    passed: subjectNoCaps,
    score: subjectNoCaps ? 5 : 0,
    maxScore: 5,
    tip: subjectNoCaps ? undefined : 'Evita parole TUTTE MAIUSCOLE',
  });

  const subjectNoSpam = !containsSpamWords(subject);
  checks.push({
    id: 'subject_no_spam_words',
    label: 'Nessuna parola spam',
    passed: subjectNoSpam,
    score: subjectNoSpam ? 7 : 0,
    maxScore: 7,
    tip: subjectNoSpam
      ? undefined
      : "L'oggetto contiene parole che possono attivare i filtri spam",
  });

  const subjectNoPunct = !hasExcessPunctuation(subject);
  checks.push({
    id: 'subject_no_excess_punctuation',
    label: 'Punteggiatura equilibrata',
    passed: subjectNoPunct,
    score: subjectNoPunct ? 5 : 0,
    maxScore: 5,
    tip: subjectNoPunct ? undefined : 'Evita punteggiatura eccessiva (!!! o ???)',
  });

  // --- Body (55 pts) ---
  const bodyHasPlaceholder = placeholderPatterns.some((p) => body.includes(p));
  checks.push({
    id: 'body_personalization',
    label: 'Personalizzazione corpo',
    passed: bodyHasPlaceholder,
    score: bodyHasPlaceholder ? 10 : 0,
    maxScore: 10,
    tip: bodyHasPlaceholder ? undefined : 'Personalizza il corpo con placeholder',
  });

  const bodyLen = plainBody.trim().length;
  const bodyLenOk = bodyLen >= 50 && bodyLen <= 5000;
  checks.push({
    id: 'body_length',
    label: 'Lunghezza corpo (50-5000 caratteri)',
    passed: bodyLenOk,
    score: bodyLenOk ? 10 : 0,
    maxScore: 10,
    tip: bodyLenOk ? undefined : 'Il corpo è troppo corto o troppo lungo',
  });

  const bodyGreeting = hasGreeting(plainBody);
  checks.push({
    id: 'body_has_greeting',
    label: 'Saluto iniziale',
    passed: bodyGreeting,
    score: bodyGreeting ? 8 : 0,
    maxScore: 8,
    tip: bodyGreeting ? undefined : 'Inizia con un saluto personalizzato',
  });

  const bodyClosing = hasClosing(plainBody);
  checks.push({
    id: 'body_has_closing',
    label: 'Chiusura formale',
    passed: bodyClosing,
    score: bodyClosing ? 7 : 0,
    maxScore: 7,
    tip: bodyClosing ? undefined : 'Aggiungi una chiusura formale',
  });

  const bodyNoSpam = !containsSpamWords(plainBody);
  checks.push({
    id: 'body_no_spam_words',
    label: 'Corpo senza parole spam',
    passed: bodyNoSpam,
    score: bodyNoSpam ? 8 : 0,
    maxScore: 8,
    tip: bodyNoSpam
      ? undefined
      : "L'oggetto contiene parole che possono attivare i filtri spam",
  });

  const bodyParas = countParagraphs(body) >= 2;
  checks.push({
    id: 'body_paragraphs',
    label: 'Struttura in paragrafi',
    passed: bodyParas,
    score: bodyParas ? 7 : 0,
    maxScore: 7,
    tip: bodyParas ? undefined : 'Struttura il testo in più paragrafi',
  });

  const allPlaceholdersUsed =
    placeholders.length === 0 ||
    placeholderPatterns.every((p) => body.includes(p));
  checks.push({
    id: 'body_all_placeholders_used',
    label: 'Tutti i placeholder utilizzati',
    passed: allPlaceholdersUsed,
    score: allPlaceholdersUsed ? 5 : 0,
    maxScore: 5,
    tip: allPlaceholdersUsed ? undefined : 'Non tutti i placeholder disponibili sono utilizzati',
  });

  const totalScore = checks.reduce((sum, c) => sum + c.score, 0);

  return {
    totalScore,
    maxScore: 100,
    checks,
    grade: getGrade(totalScore),
  };
}
